/*
Usage: 
  <skio-cart-upsell item='{{ item | json | escape }}' line='{{ forloop.index }}'></skio-cart-upsell>
  <script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
  
  May want to include above module globally if element is used in cart drawer
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js'

/* v26.60 — Pink Stork brand restyle. Was a giant pink full-width slab
   that dominated the cart row. Now a small outlined pill that sits
   inline below variant info. The upsell shouldn't compete with the
   actual checkout CTA. */
/* v29.5 — fixed two wrapping/truncation bugs flagged on the cart drawer
   redesign: (1) the frequency <select> was capped at a fixed 220px and
   clipped its own text ("Every 30 D..."); switched to width:auto so it
   always sizes to its content. (2) the "Subscribe To Get X% Off" button
   had no white-space rule, so it line-wrapped awkwardly inside its
   narrow column; added nowrap and trimmed size/spacing slightly so it
   reliably fits on one line. */
const skioCartStyles = css`
  /* v27.13 - Bloom-style subscription controls. Replaces the native
     <select>, which could not be changed on iPhone Safari, with real
     buttons: a full-width "Subscribe and save" pill for one-time items,
     and a "Subscribed" chip with a "Switch to one-time" link for
     subscription items. */
  :host { display: block; margin-top: 10px; width: 100%; }
  button { font-family: inherit; -webkit-appearance: none; appearance: none; cursor: pointer; }
  .ps-sub-btn {
    display: flex; width: 100%; min-height: 40px; box-sizing: border-box;
    align-items: center; justify-content: space-between; gap: 8px;
    padding: 9px 16px; background: #FFFFFF; color: #C4156A;
    border: 1.5px solid #C4156A; border-radius: 999px;
    font-size: 13px; font-weight: 700; text-align: left;
  }
  .ps-sub-btn:hover { background: #FDF0F4; }
  .ps-sub-btn .arrow { font-size: 17px; line-height: 1; }
  .ps-why {
    display: flex; width: 100%; align-items: center; justify-content: space-between;
    margin-top: 6px; padding: 6px 2px; background: none; border: 0;
    font-size: 12px; color: #6B6266;
  }
  .ps-why .chev { display: inline-block; transition: transform .2s ease; }
  .ps-why[aria-expanded="true"] .chev { transform: rotate(180deg); }
  .ps-why-list { margin: 2px 0 0; padding: 0; list-style: none; font-size: 12px; color: #4A4246; }
  .ps-why-list li { position: relative; padding: 3px 0 3px 18px; }
  .ps-why-list li::before { content: "\\2713"; position: absolute; left: 0; color: #C4156A; font-weight: 700; }
  .ps-sub-on {
    display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
    gap: 6px 12px; padding: 9px 14px; border-radius: 14px;
    background: #FDF0F4; font-size: 12.5px; color: #1A0F14;
  }
  .ps-sub-on strong { color: #C4156A; }
  .ps-sub-off { background: none; border: 0; padding: 4px 0; font-size: 12px; color: #6B6266; text-decoration: underline; }
  .ps-freq { display: flex; flex-wrap: wrap; gap: 6px; width: 100%; }
  .ps-freq button {
    padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 600;
    background: #FFFFFF; color: #1A0F14; border: 1px solid rgba(26,15,20,.2);
  }
  .ps-freq button[aria-pressed="true"] { background: #C4156A; color: #FFFFFF; border-color: #C4156A; }
`

export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles

  static properties = {
    item: { type: Object },
    line: { type: Number },

    product: { type: Object },
    selectedVariant: { type: Object },
    key: { type: String },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    discount_format: { type: String },

    moneyFormatter: {},

    currency: { type: String },

    whyOpen: { state: true },
  }

  constructor() {
    super()

    console.log('Constructor called with initial props:', {
      line: this.line,
      item: this.item
    });

    this.item = null
    this.line = null

    this.product = null
    this.selectedVariant = null

    this.skioSellingPlanGroups = null
    this.availableSellingPlanGroups = null

    this.selectedSellingPlanGroup = null
    this.selectedSellingPlan = null

    this.whyOpen = false

    this.discountFormat = 'percent'

    this.currency = 'USD'
    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    })
  }

  upgradeButton = () => {
    if (!(this.availableSellingPlanGroups?.length > 0)) return html``
    const plan = this.availableSellingPlanGroups[0].selected_selling_plan
    const pct = this.discount(plan).percent
    return html`
      <button class="ps-sub-btn" type="button" @click=${() => this.selectSellingPlan(plan.id)}>
        <span>Subscribe and save ${pct}</span><span class="arrow" aria-hidden="true">&rarr;</span>
      </button>
      <button class="ps-why" type="button" aria-expanded=${this.whyOpen ? 'true' : 'false'} @click=${() => { this.whyOpen = !this.whyOpen }}>
        <span>Why subscribe?</span><span class="chev" aria-hidden="true">&#8964;</span>
      </button>
      ${this.whyOpen ? html`
        <ul class="ps-why-list">
          <li>Save ${pct} on every delivery</li>
          <li>Free shipping on every subscription order</li>
          <li>Skip, swap, or cancel anytime from your account</li>
        </ul>` : ''}
    `
  }

  changeFrequency = () => {
    const allowed = new Set((this.selectedVariant?.selling_plan_allocations || []).map(a => a.selling_plan_id))
    // only offer frequencies this exact variant can actually be sold on
    const plans = (this.availableSellingPlanGroups || []).flatMap(g => g.selling_plans).filter(p => allowed.has(p.id))
    const pct = this.discount(this.selectedSellingPlan).percent
    return html`
      <div class="ps-sub-on">
        <span><strong>Subscribed</strong> &middot; ${this.selectedSellingPlan.name} &middot; saving ${pct}</span>
        <button class="ps-sub-off" type="button" @click=${() => this.selectSellingPlan('')}>Switch to one-time</button>
        ${plans.length > 1 ? html`
          <div class="ps-freq" role="group" aria-label="Delivery frequency">
            ${plans.map(p => html`<button type="button" aria-pressed=${p.id == this.selectedSellingPlan.id ? 'true' : 'false'} @click=${() => this.selectSellingPlan(p.id)}>${p.name}</button>`)}
          </div>` : ''}
      </div>
    `
  }

  render() {
    if (!this.item || !this.product || !this.selectedVariant) return
    return this.selectedSellingPlan ? this.changeFrequency() : this.upgradeButton()
  }

  updated = changed => {
    console.log('Updated called with:', {
      line: this.line,
      item: this.item,
      changedProps: Array.from(changed.keys())
    });

    
    if (changed.has('item') && this.item) {
      this.fetchProduct(this.item.handle)
    }

    if (changed.has('product') && this.product) {
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(selling_plan_group => selling_plan_group.app_id === 'SKIO')

      this.selectedVariant = this.product.variants.find(variant => variant.id == this.item.variant_id)

      if (this.item.selling_plan_allocation) {
        this.selectedSellingPlanGroup = this.skioSellingPlanGroups.find(group => group.selling_plans.find(selling_plan => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id))
        this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans.find(selling_plan => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id)
      }
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups.length) {
        //update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach(group => {
          group.selected_selling_plan = group.selling_plans[0]
        })
      }
    }

    if (changed.has('selectedSellingPlan') && this.product) {
      this.updateLineItem()
    }
  }

  log = (...args) => {
    args.unshift('%c[skio cart upsell]', 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error = (...args) => {
    args.unshift('%c [skio cart upsell]', 'color: #ff0000')
    console.error.apply(console, args)
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart_form',
        section: document.getElementById('cart_form').dataset.id,
        selector: '.cart__item-list',
      },
    ]
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  updateLineItem() {
    if (this.line === null || this.line === undefined || !this.item) {
      console.log('Early return - missing line or item:', { line: this.line, item: this.item });
      return;
    }
    
    // Rest of the checks...
    const currentPlanId = this.item.selling_plan_allocation?.selling_plan?.id;
    const newPlanId = this.selectedSellingPlan?.id;
    
    if (currentPlanId === newPlanId) {
      console.log('Early return - same selling plan');
      return;
    }
    
    /* v29.5 fix: swapping the selling plan from the cart (this dropdown)
       changed the plan server-side correctly, but the visible line-item
       price never updated to reflect the new (often discounted) price.
       Root cause: this used to call `Shopify.theme.jsAjaxCart.updateView()`,
       which isn't the refresh mechanism this theme's cart actually uses —
       the real cart (Alpine `[data-cart-items]`) only repaints itself on a
       `shapes:cart:update` window event carrying freshly rendered section
       HTML (see global.bundle.js). Requesting `sections` from /cart/change.js
       and dispatching that event is what the quantity stepper and "add to
       cart" flows already rely on elsewhere in this theme — this just wires
       the subscription swap into the same system instead of a no-op call. */

    let payload = {
      line: this.line,
      quantity: this.item.quantity,
      sections: 'cart-items,cart-footer,cart-item-count,cart-live-region', // v27.13: also refresh subtotal + count, same list the quantity stepper uses
    }

    if (this.selectedSellingPlan) {
      payload.selling_plan = this.selectedSellingPlan.id
      payload.properties = {
        'Subscription Discount': this.discount(this.selectedSellingPlan).percent,
      }
    } else {
      payload.selling_plan = null
      payload.properties = {
        'Subscription Discount': null,
      }
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(cart => {
        window.dispatchEvent(
          new CustomEvent('shapes:cart:update', {
            detail: { response: cart },
          })
        )
      })
      .catch(error => {
        this.error('Failed to update selling plan from cart', error)
      })
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(id) {
    if (!id) {
      this.selectedSellingPlanGroup = null
      this.selectedSellingPlan = null
      return
    }

    let group = this.availableSellingPlanGroups.find(group => group.selling_plans.find(selling_plan => selling_plan.id == id))
    let selling_plan = group.selling_plans.find(x => x.id == id)

    if (selling_plan) {
      group.selected_selling_plan = selling_plan
      this.selectedSellingPlanGroup = group
      this.selectedSellingPlan = selling_plan
       console.log('New selectedSellingPlan:', this.selectedSellingPlan)
    } else {
      this.log("Error: couldn't find selling plan with id " + element.value + ' for variant ' + this.selectedVariant.id + ' from product ' + this.product.id + ' : ' + this.product.handle)
    }
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan) return { percent: '0%', amount: 0 }

    const price_adjustment = selling_plan.price_adjustments[0]
    const discount = { percent: '0%', amount: 0 }
    const price = this.selectedVariant.price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = `${Math.round(((price_adjustment.value * 1.0) / price) * 100.0)}%`
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = `${Math.round((((price - price_adjustment.value) * 1.0) / price) * 100.0)}%`
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  fetchProduct = async handle => {
    let productCache = window.sessionStorage.skioCartProductCache ? JSON.parse(window.sessionStorage.skioCartProductCache) : []
    let cachedProduct = productCache ? productCache.find(product => product.handle == handle) : null

    if (cachedProduct) {
      this.product = cachedProduct
    } else {
      await fetch(`/products/${handle}.js`)
        .then(response => response.json())
        .then(response => {
          this.product = response
          productCache.push(response)
          window.sessionStorage.skioCartProductCache = JSON.stringify(productCache)
          this.requestUpdate()
        })
    }
  }
}

customElements.define('skio-cart-upsell', SkioCartUpsell)